/**
 * Capture primitives — camera, photo library, video (spec § 7).
 *
 * All of this runs offline. A file input and a canvas need no network, which is
 * why capture is the one thing guaranteed to work in a Canton Fair hall.
 */

import { PHOTO_MAX_PX, PHOTO_QUALITY } from '../data/constants';
import { checkVideo } from '../data/media';

/**
 * Downscale to 1280px on the long edge and re-encode as JPEG 0.82 (spec § 7).
 *
 * This is not an optimisation, it is the difference between a trip fitting in
 * device storage and not: a modern phone photo is 3–6MB, and this brings it to
 * roughly 150–300KB. 100 suppliers × 4 photos is ~100MB instead of ~2GB.
 *
 * Returns a Blob, not a dataURL — the prototype's base64 would add a third
 * again and cannot be stored server-side (Mongo's 16MB document ceiling).
 */
export function resizeImage(file, max = PHOTO_MAX_PX) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      let { width: w, height: h } = img;
      const scale = Math.min(1, max / Math.max(w, h));
      w = Math.round(w * scale);
      h = Math.round(h * scale);

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);

      canvas.toBlob(
        (blob) => {
          if (blob) resolve({ blob, width: w, height: h });
          else reject(new Error('Could not encode the photo'));
        },
        'image/jpeg',
        PHOTO_QUALITY,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file is not an image we can read'));
    };
    img.src = url;
  });
}

/**
 * Open a file picker and return resized photos.
 *
 * `capture: true` asks for the camera directly and allows ONE file — that is
 * how the OS camera behaves. Library picks allow many.
 */
export function pickPhotos({ capture = false } = {}) {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = !capture;
    if (capture) input.setAttribute('capture', 'environment');

    input.onchange = async () => {
      const out = [];
      for (const file of Array.from(input.files || [])) {
        try {
          const { blob, width, height } = await resizeImage(file);
          out.push({ blob, width, height });
        } catch (e) {
          /* skip an unreadable file rather than failing the whole batch */
        }
      }
      resolve(out);
    };
    // A cancelled picker never fires change; the promise simply never settles,
    // which is fine — the caller is an event handler, not a lock.
    input.click();
  });
}

/** Read a video's duration by loading its metadata into a detached element. */
export function videoDuration(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      const d = v.duration;
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(d) ? d : null);
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null); // unknown duration — rule V1 then falls back to size
    };
    v.src = url;
  });
}

/**
 * Pick a video and enforce rule V1 BEFORE it is stored: ≤20s (+2s tolerance)
 * and ≤25MB. A rejected clip returns a reason for the toast and no blob — the
 * spec is explicit that an oversized video must not be stored at all.
 */
export function pickVideo({ capture = true } = {}) {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    if (capture) input.setAttribute('capture', 'environment');

    input.onchange = async () => {
      const file = (input.files || [])[0];
      if (!file) {
        resolve(null);
        return;
      }
      const duration = await videoDuration(file);
      const verdict = checkVideo({ duration, size: file.size });
      if (!verdict.ok) {
        resolve({ rejected: verdict.reason });
        return;
      }
      resolve({ blob: file, duration, size: file.size });
    };
    input.click();
  });
}

/** Any file, for the WeChat QR image and review document uploads. */
export function pickFile({ accept = 'image/*', multiple = false } = {}) {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.multiple = multiple;
    input.onchange = () => resolve(Array.from(input.files || []));
    input.click();
  });
}
